import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OtherArticleNamesDto, PickListDataDto } from '@scip/api';
import { MasterDataService, NotificationService } from '../../../services';
import { v4 } from 'uuid';
import { translationKeys } from '../../../core';
import { typeField } from '@avano/avano-components/utils';
import { TableColumnConfiguration } from '@avano/avano-components/avano-table';

@Component({
  selector: 'scip-other-name-dialog',
  templateUrl: './other-name-dialog.component.html',
  styleUrls: ['./other-name-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtherNameDialogComponent implements OnInit {
  @Input()
  set otherNames(val: OtherArticleNamesDto[]) {
    if (val && val.length !== 0) {
      this.rowData = this.mapToRowData([...val]);
    }
  }

  @Output() readonly cancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly saveEvent: EventEmitter<OtherArticleNamesDto[]> = new EventEmitter<OtherArticleNamesDto[]>();

  columnKeys = {
    pickListData: typeField<OtherArticleNamesDto>('pickListData'),
    text: typeField<OtherArticleNamesDto>('text')
  };

  columnConfig: TableColumnConfiguration[] = [
    {
      header: translationKeys.otherNameDialogComponent.ts.column.identifierType,
      field: this.columnKeys.pickListData
    },
    {
      header: translationKeys.otherNameDialogComponent.ts.column.identifierValue,
      field: this.columnKeys.text
    }
  ];

  deleteDisabled = true;
  loading = true;
  otherNameTypesSuggestions: PickListDataDto[] = [];
  selectedIdentifier: { id: string } & OtherArticleNamesDto;
  showDialog = true;
  totalCount = 0;
  rowData: Array<{ id: string } & OtherArticleNamesDto> = [];
  translationKeys: typeof translationKeys = translationKeys;

  private otherNameTypes: PickListDataDto[] = [];

  constructor(private masterDataService: MasterDataService, private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.initializeData();
    this.initializeTable();
  }

  addIdentifier(): void {
    this.rowData.push({ id: v4(), pickListData: {}, text: '' });
    this.rowData = [...this.rowData];
  }

  deleteIdentifier(): void {
    if (this.selectedIdentifier) {
      this.rowData.splice(
        this.rowData.findIndex((data) => data.id === this.selectedIdentifier.id),
        1
      );
      this.rowData = [...this.rowData];
      this.selectedIdentifier = undefined;
      this.updateDeleteButtonStatus();
    }
  }

  emitOtherNames(): void {
    if (this.checkValidation()) {
      const identifiers: OtherArticleNamesDto[] = this.rowData.map(({ pickListData, text }) => ({ pickListData, text }));
      this.saveEvent.emit(identifiers);
    } else {
      this.notificationService.showError(translationKeys.otherNameDialogComponent.html.notification.validationError);
    }
  }

  filterOtherNameTypesList(event: { query: string }): void {
    this.otherNameTypesSuggestions = this.otherNameTypes.filter((singleID: PickListDataDto) => {
      return singleID.text.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }

  updateDeleteButtonStatus(): void {
    this.deleteDisabled = !this.selectedIdentifier;
  }

  private checkValidation(): boolean {
    return !this.rowData.find((data) => !(data.pickListData?.text && data.text?.trim().length));
  }

  private initializeTable(): void {
    if (this.rowData.length > 1) {
      this.totalCount = this.rowData.length;
    }
    this.loading = false;
  }

  private initializeData() {
    this.masterDataService.getOtherArticleNameTypes().subscribe((data: PickListDataDto[]) => {
      this.otherNameTypes = data;
    });
  }

  // We need to add additional custom unique key to identify a row for selection as two identifiers can have exactly the same value
  private mapToRowData(data: OtherArticleNamesDto[]): Array<{ id: string } & OtherArticleNamesDto> {
    return data.map(({ pickListData, text }) => ({
      id: v4(),
      pickListData,
      text
    }));
  }
}
